import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../services/apiClient';
import { useLocation } from 'react-router-dom';
import i18n from '../services/i18n';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

function useClinicId() {
  // Try to get ?clinic=... from the URL
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get('clinic');
}

const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'it', label: 'IT' },
];

function LanguageSelector() {
  const { i18n: i18nInstance } = useTranslation();
  const current = i18nInstance.language.split('-')[0];
  return (
    <div className="flex justify-center gap-2 mb-6">
      {LANGS.map(lang => (
        <button
          key={lang.code}
          onClick={() => i18n.changeLanguage(lang.code)}
          className={`px-3 py-1 rounded font-semibold border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2
            ${current === lang.code ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );
}


export default function PatientRegister() {
  const { t } = useTranslation('common');
  const clinicId = useClinicId();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    birthdate: '',
    assurance_number: '',
  });
  const [formError, setFormError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // For birthdate dropdowns
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1925 + 1 }, (_, i) => 1925 + i);
  const monthKeys = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];
  const months = monthKeys.map(key => t(`patients.form.months.${key}`));
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // Default: 01 janvier 2000
  const [birthDay, setBirthDay] = useState(1);
  const [birthMonth, setBirthMonth] = useState(0); // 0-indexed
  const [birthYear, setBirthYear] = useState(2000);

  // Update formData.birthdate when dropdowns change
  React.useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      birthdate: `${birthYear}-${String(birthMonth + 1).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`
    }));
  }, [birthDay, birthMonth, birthYear]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSuccess(false);
    setLoading(true);
    try {
      if (!clinicId) {
        setFormError(t('patients.notifications.saveError') + ': Missing clinic ID');
        setLoading(false);
        return;
      }
      const dataToSend = {
        ...formData,
        birthdate: formData.birthdate || null,
        clinic_id: clinicId,
      };
      await apiClient.post('/api/patient', dataToSend);
      setSuccess(true);
      setFormData({ name: '', address: '', birthdate: '', assurance_number: '' });
    } catch (error) {
      setFormError(t('patients.notifications.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow p-6">
        <LanguageSelector />
        <h1 className="text-2xl font-semibold text-center mb-1 text-gray-900">
          {t('patients.add')}
        </h1>
        {clinicId && (
          <div className="text-center text-xl text-gray-500 mb-5">{clinicId}</div>
        )}
        {success ? (
          <div className="rounded-md bg-green-50 p-4 text-green-700 text-sm text-center">
            {t('patients.notifications.createSuccess')}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('patients.form.name')}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label htmlFor="assurance_number" className="block text-sm font-medium text-gray-700">
                {t('patients.form.insurance')}
              </label>
              <input
                type="text"
                id="assurance_number"
                name="assurance_number"
                value={formData.assurance_number}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700">
                {t('patients.form.birthdate')}
              </label>
              <div className="flex gap-2 mt-1">
                {/* Day Dropdown */}
                <Menu as="div" className="relative inline-block text-left flex-1">
                  {({ open }) => (
                    <>
                      <Menu.Button className="inline-flex w-full justify-between items-center rounded-md bg-[rgb(197,214,248)] px-2 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                        {String(birthDay).padStart(2, '0')}
                        <ChevronDownIcon className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${open ? 'transform rotate-180' : ''}`} aria-hidden="true" />
                      </Menu.Button>
                      <Menu.Items className="absolute left-0 z-10 mt-2 w-full origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1 max-h-48 overflow-y-auto">
                          {days.map(day => (
                            <Menu.Item key={day}>
                              {({ active }) => (
                                <button
                                  onClick={() => setBirthDay(day)}
                                  className={`${active ? 'bg-[rgb(197,214,248)]' : ''} group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                >
                                  {String(day).padStart(2, '0')}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </>
                  )}
                </Menu>
                {/* Month Dropdown */}
                <Menu as="div" className="relative inline-block text-left flex-1">
                  {({ open }) => (
                    <>
                      <Menu.Button className="inline-flex w-full justify-between items-center rounded-md bg-[rgb(197,214,248)] px-2 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                        {months[birthMonth]}
                        <ChevronDownIcon className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${open ? 'transform rotate-180' : ''}`} aria-hidden="true" />
                      </Menu.Button>
                      <Menu.Items className="absolute left-0 z-10 mt-2 w-full origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1 max-h-48 overflow-y-auto">
                          {months.map((month, idx) => (
                            <Menu.Item key={month}>
                              {({ active }) => (
                                <button
                                  onClick={() => setBirthMonth(idx)}
                                  className={`${active ? 'bg-[rgb(197,214,248)]' : ''} group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                >
                                  {month}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </>
                  )}
                </Menu>
                {/* Year Dropdown */}
                <Menu as="div" className="relative inline-block text-left flex-1">
                  {({ open }) => (
                    <>
                      <Menu.Button className="inline-flex w-full justify-between items-center rounded-md bg-[rgb(197,214,248)] px-2 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                        {birthYear}
                        <ChevronDownIcon className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${open ? 'transform rotate-180' : ''}`} aria-hidden="true" />
                      </Menu.Button>
                      <Menu.Items className="absolute left-0 z-10 mt-2 w-full origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1 max-h-48 overflow-y-auto">
                          {years.map(year => (
                            <Menu.Item key={year}>
                              {({ active }) => (
                                <button
                                  onClick={() => setBirthYear(year)}
                                  className={`${active ? 'bg-[rgb(197,214,248)]' : ''} group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                >
                                  {year}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </>
                  )}
                </Menu>
              </div>
            </div>
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                {t('patients.form.address')}
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
              />
            </div>
            {formError && (
              <div className="rounded-md bg-red-50 p-4 text-red-700 text-sm">
                {formError}
              </div>
            )}
            <button
              type="submit"
              className="w-full flex justify-center rounded-md bg-teal-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              disabled={loading}
            >
              {loading ? t('common.saving') : t('patients.form.create_button')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
} 